import {HasSubscriptions} from '../../has-subscriptions';
import {EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Pagination} from '../../../classes/pagination';
import {SelectOption} from '../../forms/select/select.component';
import {Helper} from '../../../classes/helper';
import {HasMinMax} from '../../forms/min-max-slider/min-max-slider.component';
import {CommunicationService} from '../../../services/communication.service';
import {GoogleMapsService} from '../../../services/google-maps.service';
import {PreferencesService} from '../../../services/preferences.service';
import {PositionInfo} from '../../../classes/user-preferences';
import {ListingCategory, SearchType, SCREEN_SIZE} from '../../../classes/enums';
import {ActivatedRoute} from '@angular/router';
import {AgentService} from '../../../services/agent.service';
import {AgentFilters} from '../../../classes/agent';
import {TranslateService} from '@ngx-translate/core';
import {SearchFiltersParams} from '../../../classes/search-filters-params';
import { take, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {isEqual} from 'lodash-es';

export class FiltersSearch extends HasSubscriptions implements OnInit {
  @Input() currentShownListingType: ListingCategory;
  @Output() onFiltersChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() onPaginationReset: EventEmitter<void> = new EventEmitter<void>();
  searchType: SearchType = SearchType.OFFICE;
  static lastFilterSearch: string = '';
  filtersForm: FormGroup;
  showMore: boolean;
  placeholder: string; // Search Input Placeholder


  /**
   * We need to account for the fact that in the mobile view, we have a form listening for filter changes in two separate components
   * (filters-search, filters-search-mobile) but are related to each other. We need to pick and choose which subscriptions (if any) we're listening to
   * and how we want to deal with form changes, and filter updates
   */
  protected isListeningForFormChanges: boolean = false;
  protected isSubscribedToFilterChanges: boolean = false;

  // List of acceptable names for the area that we'll take for the reverse geo lookup in order of priority
  protected acceptablePlaceTypesForAreaInformation: string[] = ['locality', 'administrative_area_level_1'];

  protected formDebounceTimeInMs: number = 750;
  protected screenSize: SCREEN_SIZE;

  pagination: Pagination = new Pagination();

  realEstateTypeOptions: SelectOption[] = <any[]>[
    { key: 'filters.search-filters.any-real-estate-type', value: null }
  ];

  languageOptions: SelectOption[] = <any[]>[
    { key: 'filters.search-filters.any-language', value: null }
  ];

  specialtyOptions: SelectOption[] = <any[]>[
    { key: 'filters.search-filters.any-specialty', value: null }
  ];

  SearchType = SearchType;

  constructor(private fb: FormBuilder,
              protected agentService: AgentService,
              protected communicationService: CommunicationService,
              protected googleService: GoogleMapsService,
              protected preferencesService: PreferencesService,
              protected activatedRoute: ActivatedRoute,
              protected translateService: TranslateService
  ) {
    super();
  }

  @Input()
  set setSearchType(searchType) {
    this.searchType = searchType;
    this.fetchFilters();
  }

  ngOnInit(): void {
    this.filtersForm = this.fb.group({
      'searchInputText': this.fb.control(FiltersSearch.lastFilterSearch),
      'realEstateType': this.fb.control(''),
      'language': this.fb.control(''),
      'specialty': this.fb.control(''),
      'childrensMiracleNetwork': this.fb.control(false),
      'canadianBreastCancerFoundation': this.fb.control(false),
      'searchMoreFeatures': this.fb.control('')
    });

    this.agentService.getFilters().then((filters: AgentFilters) => {
      filters.realEstateTypes.forEach((realEstateType) => {
        this.realEstateTypeOptions.push(new SelectOption(realEstateType, realEstateType));
      });
      filters.languages.forEach((language) => {
        this.languageOptions.push(new SelectOption(language, language));
      });
      filters.specialties.forEach((specialty) => {
        this.specialtyOptions.push(new SelectOption(specialty, specialty));
      });
    });

    this.addSub(this.communicationService.screenSize.subscribe((s) => {
      this.screenSize = s;
      if (s === SCREEN_SIZE.MOBILE_ONLY) {
        this.fetchFilters();
        this.filtersForm.controls['searchInputText'].valueChanges.pipe(take(1)).toPromise().then(() => {
          this.onSubmit();
        });
      }
    }));
  }

  /**
   * Whether or not we want to have a subscription to listen to form changes and emitting those values back to the preferneces service
   * When we're on mobile, and dont have the filters open, we dont want to listen to form changes and instead we only emit changes when they submit
   */
  protected listenToFormChanges() {
    if (this.isListeningForFormChanges) return;
    else this.isListeningForFormChanges = true;
    this.addSub(
      this.filtersForm.valueChanges.pipe(debounceTime(this.formDebounceTimeInMs), distinctUntilChanged((a, b) => isEqual(a, b)))
      .subscribe((s) => {
      const filters = this.extractSearchParams();
      this.preferencesService.updateSearchFilters(this.searchType, filters);
      this.onFiltersChange.emit(filters);
    }));
  }

  /**
   * Retrieve the actual saved filters from the preferences
   * In the filters-search-mobile component we will override this function with a different one that doesnt use a subscription
   * since we don't want to listen to all filters updates because we'll be emitting filter changes when the form updates
   */
  protected fetchFilters() {
    this.addSub((this.searchType === SearchType.AGENT ?
      this.preferencesService.agentSearchFiltersChanged :
      this.preferencesService.officeSearchFiltersChanged
    ).subscribe((filters) => {
      if (this.currentShownListingType === ListingCategory.COMMERCIAL) {
        this._updateRealEstateFilterForPropertyType(filters, 'Commercial');
      }
      this.updateFilters(filters);
    }));
  }

  /**
   * Update the filters form values with the provided filters value
   * If there's no text/location provided, we'll search for one via your position through the Google API
   */
  protected updateFilters(filters: SearchFiltersParams) {
    if (filters && filters.features) {
      this.filtersForm.controls['language'].setValue(filters.features.language);
      this.filtersForm.controls['realEstateType'].setValue(filters.features.propertyType);
      this.filtersForm.controls['specialty'].setValue(filters.features.specialty);
      if (filters.features.civicActivity) {
        this.filtersForm.controls['childrensMiracleNetwork'].setValue(filters.features.civicActivity.childrensMiracleNetwork);
        this.filtersForm.controls['canadianBreastCancerFoundation'].setValue(filters.features.civicActivity.canadianBreastCancerFoundation);
      }
    }
    if (filters && filters.text) {
      this.filtersForm.controls['searchInputText'].setValue(filters.text);
    }
    else {
      if (!FiltersSearch.lastFilterSearch) {
        this.addSub(this.preferencesService.positionInfoChanged.pipe(take(1)).subscribe((position: PositionInfo) => {
          this.googleService.performReverseGeocode(position.physicalLocation || position.centerLatLng)
            .then(r => {
              const val = r ? r.find(place => this.acceptablePlaceTypesForAreaInformation.some((s) => place.types.indexOf(s) >= 0)) : null;
              if (val) {
                this.filtersForm.controls['searchInputText'].setValue(val.address_components[0].long_name);
              }
            })
            .catch((err) => {
              console.error(err);
            });
        }));
      }
      else {
        // Use their last value instead of their location if they've searched before
        this.filtersForm.controls['searchInputText'].setValue(FiltersSearch.lastFilterSearch);
      }
    }
  }

  /**
   * Break down the values from the filters form and set those values in the searchFiltersParam object
   */
  protected extractSearchParams(): SearchFiltersParams {
    const searchParams = new SearchFiltersParams();
    searchParams.text = this.filtersForm.controls['searchInputText'].value;
    searchParams.features = {
      language: this.filtersForm.controls['language'].value,
      propertyType: this.filtersForm.controls['realEstateType'].value,
      specialty: this.filtersForm.controls['specialty'].value,
      serviceArea: '',
      civicActivity: {
        childrensMiracleNetwork: this.filtersForm.controls['childrensMiracleNetwork'].value,
        canadianBreastCancerFoundation: this.filtersForm.controls['canadianBreastCancerFoundation'].value
      }
    };
    return searchParams;
  }

  openFilters() {
    this.communicationService.toolbar.searchFilters.next();
  }

  onSubmit() {
    if (this.screenSize === SCREEN_SIZE.MOBILE_ONLY) {
      const filters = this.extractSearchParams();
      this.preferencesService.updateSearchFilters(this.searchType, filters);
      this.onFiltersChange.emit(filters);
    }
    this.onPaginationReset.emit();
  }

  onReset() {
    Object.keys(this.filtersForm.controls).forEach((key) => {
      this.filtersForm.controls[key].patchValue('');
    });
    this.onPaginationReset.emit();
  }

  compareWithHasMinMax(item1: HasMinMax, item2: HasMinMax): boolean {
    return Helper.compareWithHasMinMax(item1, item2);
  }

  _updateRealEstateFilterForPropertyType(filters, propertyType) {
    if (filters.features) {
      filters.features.propertyType = propertyType;
      this.preferencesService.updateSearchFilters(this.searchType, filters);
      this.onFiltersChange.emit(filters);
    }
  }
}
